import React, { useState } from 'react';
import { XMarkIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { FactureProforma, ConvertProformaData } from '../../types';

interface ConvertProformaModalProps {
  proforma: FactureProforma;
  onSubmit: (data: ConvertProformaData) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const ConvertProformaModal: React.FC<ConvertProformaModalProps> = ({
  proforma,
  onSubmit,
  onClose,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ConvertProformaData>({
    date_echeance: getDefaultEcheanceDate(),
    notes: '',
  });

  function getDefaultEcheanceDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <DocumentDuplicateIcon className="h-6 w-6 mr-2 text-blue-600" />
            Convertir en facture
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Info proforma */}
        <div className="bg-orange-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-600">
            Pro Forma: <span className="font-medium text-gray-900">{proforma.numero}</span>
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Patient: <span className="font-medium text-gray-900">
              {proforma.patient_details?.nom} {proforma.patient_details?.prenoms}
            </span>
          </p>
          <p className="text-lg font-bold text-orange-600 mt-2">
            {formatMontant(proforma.montant_patient)} GNF
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            Cette action va créer une facture définitive à partir de ce devis.
            Le devis sera marqué comme "converti" et ne pourra plus être modifié.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date d'échéance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date d'échéance de la facture *
            </label>
            <input
              type="date"
              value={formData.date_echeance}
              onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 text-gray-700"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 text-gray-700"
              placeholder="Notes pour la facture..."
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50"
            >
              {isLoading ? 'Conversion...' : 'Créer la facture'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConvertProformaModal;
